function createCallbackName(): string {
  const callbackName: string = `cb${Date.now()}`;
  if (callbackName in window) return createCallbackName();
  return callbackName
}

interface ScriptRequestParams<T = unknown>{
  url: string;
  params?: Record<string, string | number | boolean>;
  callbackName?: string;
  onSuccess?: (result: T) => void;
  onError?: (url: string) => void;
}

type ScriptElementWithIE = HTMLScriptElement & {
  onreadystatechange?: (() => void) | null;
  readyState?: string;
}

const TIME_UNTIL_CALLBACK_IS_DELETED_AFTER_FINISH: number = 60e3;

export class ScriptRequest<T = unknown> {
  private callbackName: string;
  private url: string;
  private onSuccess: (result: T) => void;
  private onError: (url: string) => void;
  private isFinished: boolean;
  private script: HTMLScriptElement | undefined;

  constructor({ url, params = {}, callbackName, onSuccess = () => {}, onError = () => {} }: ScriptRequestParams<T>) {
    this.callbackName = callbackName || createCallbackName();
    this.url = this._updateUrl(url, params);
    this.onSuccess = onSuccess;
    this.onError = onError;
    this.isFinished = false;
    this.script = undefined;

    this._send();
  }

  abort = (): void => this._endWork();

  private _send = (): void => {
    (window as unknown as Record<string, unknown>)[this.callbackName] = this._onSuccess;
    this.script = document.createElement('script');
    this._createErrorCatchers();
    this.script.src = this.url;
    document.body.appendChild(this.script);
  }

  private _updateUrl = (url: string, params: Record<string, string | number | boolean>): string => {
    let newUrl: string = `${url}${~url.indexOf('?') ? '&' : '?'}`;

    /* addParamsToURL */
    let uriParams: string[] = [];
    for (let param in params) {
      if (params[param]) {
        uriParams.push(`${encodeURIComponent(param)}=${encodeURIComponent(String(params[param]))}&`);
      }
    }
    newUrl += uriParams.join();

    /* addCallbackToUrl */
    newUrl += `callback=${this.callbackName}`;

    return newUrl
  }

  private _onSuccess = (result: unknown): void => {
    if (this.isFinished) return;
    this._endWork();
    this.onSuccess(result as T);
  }

  private _createErrorCatchers = (): void => {
    if (!this.script) return;

    this.script.onload = this._checkCallback;
    this.script.onerror = this._checkCallback;
    
    /* for IE */
    const ieScript = this.script as ScriptElementWithIE;
    ieScript.onreadystatechange = (): void => {
      if (!this.script) return;
      const scriptState = (this.script as ScriptElementWithIE);
      if (scriptState.readyState === 'complete' || scriptState.readyState === 'loaded') {
        ieScript.onreadystatechange = null;
        setTimeout(this._checkCallback, 0);
      }
    }
  }

  private _checkCallback = (): void => {
    if (this.isFinished) return;
    this._endWork();
    this.onError(this.url);
  }

  private _endWork = (): void => {
    this.isFinished = true;
    setTimeout((): void => {
      delete (window as unknown as Record<string, unknown>)[this.callbackName];
    }, TIME_UNTIL_CALLBACK_IS_DELETED_AFTER_FINISH)
    this.script && document.body.appendChild(this.script);
  }
}

export class ScriptRequests {
  private requests: Record<string, ScriptRequest<unknown>> = {};

  constructor() {
    this.requests = {};
  }

  send = <T = unknown> ({ url, params, callbackName, onSuccess, onError }: ScriptRequestParams<T>): void => {
    const requestKey: string = callbackName || createCallbackName();
    this.requests[requestKey] = new ScriptRequest<T>({
      url,
      params,
      callbackName,
      onSuccess: (event: T): void => {
        delete this.requests[requestKey];
        onSuccess?.(event);
      },
      onError: (event: string): void => {
        delete this.requests[requestKey];
        onError?.(event);
      },
    }) as ScriptRequest<unknown>;
  }

  abort = (): void => {
    Object.keys(this.requests).forEach((requestKey: string): void => {
      this.requests[requestKey].abort();
      delete this.requests[requestKey];
    });
  }
}
